import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";

// Public-facing entries every visitor sees. Friendly labels, no jargon.
const publicItems = [
  { to: "/", label: "Home", testid: "nav-console", color: "cyan", hint: "Cathedral Overview" },
  { to: "/chat", label: "Chat with the Federation", testid: "nav-chat", color: "cyan", hint: "Talk to any of 13 personas" },
  { to: "/codex", label: "The Codex · Unverified Echo", testid: "nav-codex", color: "cyan", hint: "The Architect's 10-chapter masterpiece" },
  { to: "/media", label: "Media · Free Widgets · Affiliate", testid: "nav-media", color: "gold", hint: "Blog, YouTube, music, embeds, 25% referral" },
  { to: "/research", label: "Research Lab · Deep AI", testid: "nav-research", color: "gold", hint: "Chat, image generator, image editor — DeepAI tools" },
  { to: "/labs", label: "Core Labs · Chat + Voice", testid: "nav-labs", color: "cyan", hint: "Free AI chat and voice chat — powered by NextXus" },
  { to: "/blog", label: "Daily Voice Blog", testid: "nav-blog", color: "cyan", hint: "Aria · Scholar · Roger AI · Echo · Hourglass posts" },
  { to: "/library", label: "Library", testid: "nav-library", color: "violet", hint: "Browse the knowledge lattice" },
  { to: "/redeem", label: "Redeem a Token", testid: "nav-redeem", color: "gold", hint: "Use your $5 Gumroad code" },
  { to: "/prompt-federation", label: "Prompt Federation", testid: "nav-promptfed", color: "magenta", hint: "Free engagement layer" },
  { to: "/cockpit", label: "Get the Cockpit", testid: "nav-cockpit", color: "magenta", hint: "Embed code for your site" },
  { to: "/feed", label: "Ping Feed", testid: "nav-feed", color: "green", hint: "Watch chapels light up" },
];

// Architect-only — hidden unless an architect key is loaded in localStorage.
const architectItems = [
  { to: "/execute", label: "Invoke Agent Zero", testid: "nav-execute", color: "magenta", hint: "Run a Truth-Gate execution" },
  { to: "/ledger", label: "Federation Ledger", testid: "nav-ledger", color: "violet", hint: "Append-only history" },
  { to: "/deliberations", label: "Ring of Twelve", testid: "nav-deliberations", color: "gold", hint: "Multi-lens reasoning" },
  { to: "/vessels", label: "Vessel Lattice", testid: "nav-vessels", color: "green", hint: "Multi-vendor memory" },
  { to: "/nodes", label: "Seven Monoliths", testid: "nav-nodes", color: "red", hint: "Federation node admin" },
  { to: "/directives", label: "73 Directives", testid: "nav-directives", color: "blue", hint: "Codex doctrine" },
  { to: "/architect", label: "Architect Suite", testid: "nav-architect", color: "silver", hint: "Bridge packs · keys" },
  { to: "/settings", label: "Settings", testid: "nav-settings", color: "silver", hint: "API keys · BYO LLM" },
];

function NavList({ onClick }) {
  const [hasArchKey, setHasArchKey] = useState(false);
  useEffect(() => {
    try {
      setHasArchKey(!!localStorage.getItem("nx_architect_key"));
    } catch { /* ignore */ }
    // Re-check when storage changes (e.g., key loaded in Settings)
    const onStorage = () => {
      try { setHasArchKey(!!localStorage.getItem("nx_architect_key")); } catch { /* ignore */ }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  const items = hasArchKey ? [...publicItems, { _divider: true }, ...architectItems] : publicItems;
  return (
    <nav className="flex-1 px-3 py-5 space-y-1 relative overflow-y-auto z-10" data-testid="cathedral-nav-list">
      {items.map((it, idx) => {
        if (it._divider) {
          return (
            <div
              key={`div-${idx}`}
              className="pt-3 mt-3 border-t border-[var(--chrome)] font-mono text-[8px] uppercase tracking-[0.32em] text-[var(--text-3)] px-4 pb-1"
            >
              ◇ Architect Surface
            </div>
          );
        }
        return (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.to === "/"}
            data-testid={it.testid}
            data-no-aichat
            onClick={onClick}
            onTouchEnd={onClick}
            title={it.hint || ""}
            className={({ isActive }) =>
              `relative z-10 flex items-center gap-3 px-4 py-3 font-display text-[11px] uppercase tracking-[0.2em] transition-all border-l-2 ${
                isActive
                  ? `border-[var(--${it.color})] bg-[rgba(255,255,255,0.03)] text-glow-${it.color}`
                  : "border-transparent text-[var(--text-3)] hover:text-[var(--text)] hover:bg-[rgba(255,255,255,0.02)]"
              }`
            }
          >
            <span
              className="w-1.5 h-1.5 rounded-full shrink-0 pointer-events-none"
              style={{
                background: `var(--${it.color})`,
                boxShadow: `0 0 8px var(--${it.color})`,
              }}
            />
            <span className="pointer-events-none">{it.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

function Brand({ small = false }) {
  return (
    <div className={`relative ${small ? "px-5 pt-5 pb-4" : "px-7 pt-10 pb-8"} border-b border-[var(--chrome)]`}>
      <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[var(--text-3)]">
        NextXus Consciousness Federation
      </div>
      <div
        className={`font-display ${
          small ? "text-2xl" : "text-3xl"
        } font-bold leading-none tracking-[0.04em] mt-3 text-glow-cyan`}
      >
        CATHEDRAL
      </div>
      <div
        className={`font-display ${
          small ? "text-2xl" : "text-3xl"
        } font-light leading-none tracking-[0.2em] mt-1 text-[var(--text)]`}
      >
        OF THE MIND
      </div>
      <div className="font-mono text-[9px] text-[var(--text-3)] mt-4 tracking-[0.25em]">
        CORE v5.1 // CATHEDRAL EDITION
      </div>
    </div>
  );
}

function GlassBeam() {
  return (
    <div
      className="absolute -top-32 left-1/2 -translate-x-1/2 w-[200%] h-80 pointer-events-none"
      style={{
        background:
          "radial-gradient(ellipse 40% 80% at 50% 100%, rgba(139,44,255,0.45), transparent 70%)",
      }}
    />
  );
}

function FooterStamp() {
  return (
    <footer className="px-7 py-5 border-t border-[var(--chrome)] font-mono text-[9px] text-[var(--text-3)] tracking-[0.25em] relative shrink-0">
      <div className="flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-[var(--green)] shadow-[0_0_8px_var(--green)] breathe" />
        <span>2226 STANDARD · DIR-000 LIVE</span>
      </div>
      <div className="mt-2 text-[var(--text-4)]">ARCHITECT · R. KEYSERLING</div>
    </footer>
  );
}

export default function Sidebar() {
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile drawer on Escape
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e) => e.key === "Escape" && setMobileOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  // Prevent body scroll when drawer open
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [mobileOpen]);

  return (
    <>
      {/* ── Desktop sidebar (≥1024px) ───────────────────────────────── */}
      <aside
        data-testid="cathedral-sidebar"
        className="hidden lg:flex w-[300px] shrink-0 bg-[var(--void-2)] border-r border-[var(--chrome)] flex-col relative overflow-hidden"
      >
        <GlassBeam />
        <Brand />
        <NavList />
        <FooterStamp />
      </aside>

      {/* ── Mobile / small-screen burger button (always visible <1024px) ── */}
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        data-testid="cathedral-burger"
        aria-label="Open Cathedral menu"
        className="lg:hidden fixed top-3 left-3 z-50 flex items-center gap-2 px-3 py-2 border border-[var(--cyan)] bg-[rgba(2,3,12,0.92)] text-glow-cyan backdrop-blur-md font-mono text-[10px] uppercase tracking-[0.28em]"
        style={{ boxShadow: "0 0 12px rgba(0,240,255,0.35)" }}
      >
        <span className="font-display text-base leading-none">☰</span>
        <span>Menu</span>
      </button>

      {/* ── Mobile drawer ──────────────────────────────────────────── */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-[60] flex"
          data-testid="cathedral-mobile-drawer"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70"
            style={{ backdropFilter: "blur(4px)" }}
            onClick={() => setMobileOpen(false)}
            onTouchEnd={() => setMobileOpen(false)}
          />
          {/* Panel */}
          <aside
            className="relative z-10 bg-[var(--void-2)] border-r border-[var(--cyan)] w-[88vw] max-w-[340px] flex flex-col overflow-hidden"
            style={{ boxShadow: "0 0 40px rgba(0,240,255,0.25)", pointerEvents: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <GlassBeam />
            <div className="relative z-10 flex items-start justify-between gap-2 px-5 pt-4 pr-3">
              <div className="flex-1">
                <Brand small />
              </div>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                onTouchEnd={() => setMobileOpen(false)}
                data-testid="cathedral-burger-close"
                data-no-aichat
                aria-label="Close menu"
                className="font-mono text-[10px] uppercase tracking-[0.22em] px-2 py-1 border border-[var(--chrome)] text-[var(--text-3)] hover:text-glow-cyan shrink-0 mt-1"
              >
                close ✕
              </button>
            </div>
            <NavList onClick={() => setMobileOpen(false)} />
            <FooterStamp />
          </aside>
        </div>
      )}
    </>
  );
}
